"use client";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { CustomMcpListing } from "../../api/customMcpApi";
import type { ApiKeyListing, PatchApiKeyInput } from "../../api/apiKeysApi";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The key row being edited. Fields prefilled from this. */
  target: ApiKeyListing | null;
  /** Called after a successful PATCH so the parent can refresh. */
  onSaved: () => void;
}

// Edit an embed key. Fields limited to what the backend PATCH accepts
// (name, allowed_origins, config, model) — auth_mode + JWT settings are
// immutable after create (rotating those requires revoke + recreate).
export function EditEmbedKeyDialog({ open, onClose, target, onSaved }: Props) {
  const { apiKeys, customMcp } = useAuthedApi();
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  const [model, setModel] = useState("");
  const [grantableSkills, setGrantableSkills] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [connectors, setConnectors] = useState<CustomMcpListing[]>([]);
  const [selectedMcp, setSelectedMcp] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const csv = (s: string) =>
    s.split(",").map((x) => x.trim()).filter(Boolean);

  // Prefill whenever a new target opens.
  useEffect(() => {
    if (!open || !target) return;
    setName(target.name);
    setOrigins(target.allowed_origins.join(", "));
    setModel(target.model ?? "");
    setSelectedSkills(new Set(target.config?.components?.skills ?? []));
    setSelectedMcp(new Set(target.config?.components?.mcp_servers ?? []));
    setError(null);
  }, [open, target]);

  // Refresh picker sources when the dialog opens.
  useEffect(() => {
    if (!open) return;
    void customMcp
      .list("company")
      .then(setConnectors)
      .catch(() => setConnectors([]));
    void apiKeys
      .grantablePlugins()
      .then((c) => setGrantableSkills(c.skills ?? []))
      .catch(() => setGrantableSkills([]));
  }, [open, customMcp, apiKeys]);

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const components: Record<string, string[]> = {};
      if (selectedSkills.size) components.skills = [...selectedSkills];
      if (selectedMcp.size) components.mcp_servers = [...selectedMcp];
      const input: PatchApiKeyInput = {
        name: name.trim(),
        // Send an empty components map (not undefined) when the picker is
        // cleared — the backend's key.config ⊆ install re-check needs an
        // explicit new value to widen/narrow.
        config: { components },
        allowed_origins: csv(origins),
        model: model.trim() || null,
      };
      await apiKeys.patch(target.id, input);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to save key");
    } finally {
      setBusy(false);
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit embed key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            Auth mode ({target.auth_mode}) is fixed after create. To change it,
            revoke this key and create a new one.
          </Alert>
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          <Divider textAlign="left">
            <Typography variant="caption">Capabilities (⊆ installed)</Typography>
          </Divider>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Skills
            </Typography>
            {grantableSkills.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No skills installed for this company.
              </Typography>
            ) : (
              <Stack sx={{ maxHeight: 160, overflowY: "auto", pl: 0.5 }}>
                {grantableSkills.map((s) => (
                  <FormControlLabel
                    key={s}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedSkills.has(s)}
                        onChange={() =>
                          setSelectedSkills((prev) => {
                            const next = new Set(prev);
                            next.has(s) ? next.delete(s) : next.add(s);
                            return next;
                          })
                        }
                      />
                    }
                    label={<Typography variant="body2">{s}</Typography>}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Connectors
            </Typography>
            {connectors.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No connectors installed for this company.
              </Typography>
            ) : (
              <Stack sx={{ maxHeight: 180, overflowY: "auto", pl: 0.5 }}>
                {connectors.map((c) => (
                  <FormControlLabel
                    key={c.name}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedMcp.has(c.name)}
                        onChange={() =>
                          setSelectedMcp((prev) => {
                            const next = new Set(prev);
                            next.has(c.name)
                              ? next.delete(c.name)
                              : next.add(c.name);
                            return next;
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {c.display_name || c.name}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Divider textAlign="left">
            <Typography variant="caption">Access</Typography>
          </Divider>
          <TextField
            label="Allowed origins (comma-separated)"
            size="small"
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            helperText="Sites allowed to embed. Empty = server-to-server only."
          />
          <TextField
            label="Model (blank = Auto)"
            size="small"
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !name.trim()}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
